import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { PUTparticipants, removeParticipant } from "../../api/reportsApi"; // ⬅️ add updateParticipant
import { TextAreaInputShort } from "../../components/atomic/commentsInput";
import { DiscussionComponent, EditIcon } from "../../components/atomic/discussionComponent";
import { RemoveItemComponent } from "../../components/atomic/removeItemComponent";
import { ParticipantAnswer } from "../../types/Rows";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../../components/ui/Modal";
import { btn, cx } from "../../components/ui/styles";


type ParticipantForm = {
  id: string;
  participantNumber: string;
  visitNumber: string;
  isFinshed: boolean; // (typo preserved to match your API/DB)
  isVerified: boolean;
  comment: string;
};

export function EditParticipantModal({
  open,
  onClose,
  initial,
  onDelete,
  onSave,
  title = "Edytuj uczestnika",
}: {
  open: boolean;
  onClose: () => void;
  initial: ParticipantForm;
  onDelete?: () => Promise<void>;
  onSave: (next: ParticipantForm) => Promise<void>;
  title?: string;
}) {
  const [form, setForm] = useState<ParticipantForm>(initial);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setError(null);
    }
  }, [open, initial]);

  const setField = <K extends keyof ParticipantForm>(k: K, v: ParticipantForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Nie udało się zapisać.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Nie udało się usunąć.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !saving && onClose()}>
      <ModalHeader title={title} onClose={() => !saving && onClose()} />
      <ModalBody>
        <TextAreaInputShort label="Numer uczestnika"  value={form.participantNumber} onChange={() => {}} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextAreaInputShort
            label="Numer wizyty/fazy leczenia"
            value={form.visitNumber}
            onChange={(v: string) => setField("visitNumber", v)}
            maxLength={512}
          />

          <div className="flex items-center gap-6 pt-6 sm:pt-0">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isFinshed}
                onChange={() => setField("isFinshed", !form.isFinshed)}
              />
              <span>100%</span>
            </label>
            <label className="inline-flex items-center gap-2" title="RBV">
              <input
                type="checkbox"
                checked={form.isVerified}
                onChange={() => setField("isVerified", !form.isVerified)}
              />
              <span>RBV</span>
            </label>
          </div>
        </div>

        <TextAreaInputShort
          label="Komentarz"
          value={form.comment}
          onChange={(v: string) => setField("comment", v)}
          maxLength={8000}
          rowSize={3}
        />

        {error && <p className="text-sm text-red-600">{error}</p>}
      </ModalBody>

      <ModalFooter
        onDelete={handleDelete}
        onSave={handleSave}   // uses default Save/Cancel from your ModalFooter
        onCancel={() => !saving && onClose()}
        saveLoading={saving}
      />
    </Modal>
  );
}


export function ParticipantRowWithEdit({
  input,
  reportId,
}: {
  input: ParticipantAnswer;
  reportId: string;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { mutateAsync: remove } = useMutation(
    async (id: string) => removeParticipant(reportId, id),
    { onSuccess: () => qc.invalidateQueries([reportId, "Participants"]) }
  );

  // NEW: PUT mutation
  const { mutateAsync: putParticipant, isLoading: isSavingPut } = useMutation(
    async (body: {
      visitNumber: string;
      isFinshed: boolean;
      isVerified: boolean;
      comment: string;
    }) => PUTparticipants(reportId, [{     
       ...input,
      ...body} as ParticipantAnswer], null),
    { retry: 0, onSuccess: () => qc.invalidateQueries([reportId, "Participants"]) }
  );

  // Prepare modal initial form
  const initial: ParticipantForm = useMemo(
    () => ({
      id: input.id,
      participantNumber: input.participantNumber,
      visitNumber: input.visitNumber ?? "",
      isFinshed: !!input.isFinshed,
      isVerified: !!input.isVerified,
      comment: input.comment ?? "",
    }),
    [input]
  );

  const yesno = (b?: boolean) => (b ? "Tak" : "Nie");

  return (
    <>
      {/* READ-ONLY ROW */}
      <div className="flex items-center border-t border-[var(--sc-border)] px-4 py-3.5 text-sm hover:bg-[var(--sc-bg)] transition-colors odd:bg-[var(--sc-surface)] even:bg-[var(--sc-bg)]">
        <div className="w-[25%] break-words">{input.participantNumber || "-"}</div>
        <div className="w-[30%] break-words">{input.visitNumber || "-"}</div>

        <div className="w-[15%] flex items-center gap-3 text-xs">
          <span title="100%" className={input.isFinshed ? "text-[#2BAE72] font-medium" : "text-[var(--sc-muted)]"}>100%</span>
          <span title="RBV" className={input.isVerified ? "text-[#2BAE72] font-medium" : "text-[var(--sc-muted)]"}>RBV</span>
        </div>

        <div className="w-[25%] break-words text-[var(--sc-muted)]">{input.comment || "-"}</div>

        <div className="ml-auto flex items-center gap-2">
          <DiscussionComponent resourceId={input.id} />
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cx(btn.icon, "text-[var(--sc-muted)] hover:bg-[var(--sc-bg)] hover:text-[var(--sc-text)]")}
            aria-label="Edytuj"
            title="Edytuj"
          >
            <EditIcon />
          </button>
        </div>
      </div>

      {/* MODAL */}
      <EditParticipantModal
        open={open}
        onClose={() => setOpen(false)}
        initial={initial}
        onDelete={async () => {
          await remove(input.id);
        }}
        onSave={async (next) => {
          // ⬇️ now performs PUT to your /reportsApi
          await putParticipant({
            visitNumber: next.visitNumber,
            isFinshed: next.isFinshed,
            isVerified: next.isVerified,
            comment: next.comment,
          });
        }}
        title={`Edytuj uczestnika #${input.participantNumber || input.id.slice(0, 6)}`}
      />
    </>
  );
}
